/**
 * Onboarding **form** domain types, shared between server and web.
 *
 * A form is HR-authored: a multi-page set of fields (text, dropdowns, a
 * uniform sizing grid, file uploads, etc) that an inductee fills in. It is
 * the configurable replacement for the old hardcoded onboarding_kind capture
 * forms. Like onboarding documents, a form is **versioned**: publishing
 * inserts a new immutable version row, so a submission always resolves to the
 * exact schema it was filled against.
 *
 * Storage rules (same as the rest of onboarding, see ./onboarding.ts):
 *   - The *definition* lives in onboarding_forms / onboarding_form_versions.
 *   - A submission's *values* live in people.metadata.onboarding.forms[key]
 *     (redactable for GDPR), never on the event chain.
 *   - A PII-free proof event records that a capture happened.
 *
 * The classification boundary still applies per field: each field declares
 * which surfaces it may appear on, defaulting to onboarding/self only. See
 * FieldSurface in ./onboarding.ts.
 */

import { FIELD_SURFACES, type FieldSurface } from './onboarding';
import { maskHasToken } from './inputMask';

/* -------------------------------------------------------------------------
 * Field types
 * -----------------------------------------------------------------------*/

/**
 * Every field type the builder can place. Split conceptually into:
 *   - layout/static (no value): section_heading, paragraph, image
 *   - inputs (carry a value): everything else
 */
export const FORM_FIELD_TYPES = [
  'section_heading',
  'paragraph',
  'rich_text',
  'image',
  'short_text',
  'long_text',
  'name',
  'address',
  'email',
  'phone',
  'number',
  'date',
  'dropdown',
  'radio',
  'checkbox',
  'yes_no',
  'file_upload',
  'signature',
  'grid',
  'damage_markup',
] as const;
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

/** Field types that render static content and never produce a value. */
export const STATIC_FIELD_TYPES: readonly FormFieldType[] = [
  'section_heading',
  'paragraph',
  'rich_text',
  'image',
];

/** Field types that require an `options` list (the choice sets). */
export const OPTION_FIELD_TYPES: readonly FormFieldType[] = ['dropdown', 'radio', 'checkbox'];

/** Field types that may carry an input `mask` (see ./inputMask.ts). */
export const MASKABLE_FIELD_TYPES: readonly FormFieldType[] = ['short_text', 'phone', 'number'];

/** A selectable choice for dropdown / radio / checkbox fields. */
export interface FormFieldOption {
  value: string;
  label: string;
}

/**
 * A column in a generic grid. A 'select' column needs its own options.
 * A 'checkbox' cell stores 'yes' when ticked and '' when not, so the grid
 * value stays Record<row, Record<col, string>> across every cell type.
 */
export const GRID_CELL_TYPES = ['text', 'number', 'select', 'checkbox'] as const;
export type GridCellType = (typeof GRID_CELL_TYPES)[number];

export interface GridColumn {
  key: string;
  label: string;
  cell: GridCellType;
  /** Required when cell === 'select'. */
  options?: FormFieldOption[];
  /** Optional input mask for text/number cells (applies to every row). */
  mask?: string;
}

/**
 * The two stock categories a grid row can be tagged as for procurement.
 * Mirrors KitCategory in ./kitTemplate.ts but kept local so this module has
 * no cross-domain import.
 */
export const GRID_ORDER_CATEGORIES = ['Uniform', 'PPE'] as const;
export type GridOrderCategory = (typeof GRID_ORDER_CATEGORIES)[number];

/**
 * Procurement metadata that turns a sizing-grid row into a known orderable
 * item. Optional and admin-only: it never affects what the inductee sees or
 * submits. Its presence on any row is what makes a form an "ordering source"
 * for the Kit to order screen — each submitted size becomes demand for `item`
 * at that size, unless a gate suppresses it. The per-person quantity lives on
 * the kit item that links to this row, not here (so different roles can get
 * different counts of the same garment).
 */
export interface GridRowOrder {
  /** Canonical orderable item name, e.g. 'Polo Shirt - Black'. */
  item: string;
  category: GridOrderCategory;
  /**
   * Optional gate: the key of a yes_no field on the same form. When the
   * person's answer to that field equals `gate_skip_value`, this row is not
   * ordered for them (e.g. "I'll buy my own boots" = Yes).
   */
  gate_field_key?: string;
  gate_skip_value?: string;
}

export interface GridRow {
  key: string;
  label: string;
  /**
   * Per-row override of the column's input. When `cell` is set, this row uses
   * it (with its own options/mask) instead of the column default, so e.g. most
   * rows can be a size dropdown while one row is a masked free-text input. When
   * unset, the row inherits the column. For multi-column grids the override
   * applies to every column in the row.
   */
  cell?: GridCellType;
  options?: FormFieldOption[];
  mask?: string;
  /** Procurement tagging (admin-only); see GridRowOrder. */
  order?: GridRowOrder;
}

/** The effective input for a cell: the row override when set, else the column. */
export interface ResolvedGridCell {
  cell: GridCellType;
  options?: FormFieldOption[];
  mask?: string;
}
export function resolveGridCell(row: GridRow, col: GridColumn): ResolvedGridCell {
  if (row.cell) return { cell: row.cell, options: row.options, mask: row.mask };
  return { cell: col.cell, options: col.options, mask: col.mask };
}

/**
 * One freehand stroke on a damage_markup field. Points are a flat
 * [x1, y1, x2, y2, ...] list normalised to 0..1 of the background image, so
 * the drawing re-renders correctly at any display size and the flattened PNG
 * export always matches what was drawn.
 */
export interface DamageMarkupStroke {
  color: string;
  /** Line width as a fraction of the image width (e.g. 0.004). */
  width: number;
  points: number[];
}

/** The stored value of a damage_markup field. */
export interface DamageMarkupValue {
  strokes: DamageMarkupStroke[];
}

/** Coerce an unknown stored value into a DamageMarkupValue. */
export function asDamageMarkup(v: unknown): DamageMarkupValue {
  if (typeof v === 'object' && v !== null && Array.isArray((v as DamageMarkupValue).strokes)) {
    const strokes = (v as DamageMarkupValue).strokes.filter(
      (s) =>
        typeof s === 'object' &&
        s !== null &&
        typeof s.color === 'string' &&
        typeof s.width === 'number' &&
        Array.isArray(s.points),
    );
    return { strokes };
  }
  return { strokes: [] };
}

/**
 * Fully generic grid: arbitrary labelled rows x typed columns. The uniform
 * sizing table is just one configuration (rows = garments, one 'select' size
 * column).
 */
export interface GridConfig {
  rows: GridRow[];
  columns: GridColumn[];
}

/**
 * Conditional visibility. The field is shown only when the referenced field's
 * value equals one of `equals`. Stored in v1; the renderer wires it up later
 * (so no schema migration is needed when conditional logic ships).
 */
export interface FieldCondition {
  /** key of another field in the same form. */
  field: string;
  /** Show when the other field's value matches any of these. */
  equals: string[];
}

/**
 * Evaluate a visible_if condition against the current answers. Shared by the
 * inductee renderer, the builder preview, server-side submission validation and
 * progress so they all agree on what is shown. No condition means always shown.
 */
export function conditionMet(
  cond: FieldCondition | undefined,
  values: Record<string, unknown>,
): boolean {
  if (!cond) return true;
  const v = values[cond.field];
  if (Array.isArray(v)) return v.some((x) => cond.equals.includes(String(x)));
  return cond.equals.includes(String(v ?? ''));
}

/**
 * One field definition in a form page's schema. A single shape covers every
 * field type; only the keys relevant to `type` are populated (validated by
 * validateFormSchema).
 */
export interface FormFieldDef {
  /** Stable slug, unique within the whole form. Keys the submitted value. */
  key: string;
  type: FormFieldType;
  /** Visible label (or the heading text for section_heading). */
  label: string;
  /** Helper text under the input. */
  help?: string;
  required?: boolean;
  /**
   * Relax `required` when the form is filled under a mobilisation-day scope
   * (e.g. the walk-around video on a vehicle handover: mandatory for a
   * day-to-day handover, skippable in the venue-wifi crush of an induction
   * day). Ignored unless `required` is set. Self-service /me submissions
   * always run on the onboarding programme, so they enforce as normal.
   */
  optional_on_event_day?: boolean;
  /** dropdown / radio / checkbox choices. */
  options?: FormFieldOption[];
  /** grid configuration, for type === 'grid'. */
  grid?: GridConfig;
  /** Static body, for type === 'paragraph' (plain text) or 'rich_text' (HTML). */
  text?: string;
  /** Stored asset key (S3) or URL, for type === 'image'. */
  image_key?: string;
  /**
   * file_upload: allowed file extensions (lowercase, no dot), e.g.
   * ['pdf','jpg','png','docx','zip']. Empty/absent = the server default
   * allowlist. The server also enforces a hard malicious-extension blocklist.
   */
  accept?: string[];
  /** file_upload: allow more than one file. */
  multiple?: boolean;
  /**
   * Special-category (GDPR Article 9) data: ethnicity, religion, sexual
   * orientation, gender, health. Forced to onboarding/self classification and
   * requires the form-level consent statement before submission.
   */
  special_category?: boolean;
  /**
   * Surfaces this field's value may appear on. Defaults to ['self',
   * 'onboarding'] when absent. Only uniform-size style fields should add
   * 'ppe_desk'. Special-category fields may never include a desk surface.
   */
  classification?: FieldSurface[];
  /** Conditional visibility (renderer support lands later). */
  visible_if?: FieldCondition;
  /**
   * Input mask restricting the accepted format, for MASKABLE_FIELD_TYPES only.
   * `@`=letter, `#`=digit, `*`=either; other chars are literals. See
   * ./inputMask.ts. Enforced on the renderer as the user types and on submit
   * (client and server).
   */
  mask?: string;
}

/** One step/page of a multi-step form. */
export interface FormPage {
  title?: string;
  description?: string;
  fields: FormFieldDef[];
  /**
   * Show the whole page only when this condition is met. The controlling field
   * must live on an earlier page (the inductee answers it before reaching here).
   * A hidden page and all its fields are skipped everywhere: navigation,
   * validation, progress and submission.
   */
  visible_if?: FieldCondition;
}

/**
 * Consent statement an inductee must agree to before submitting. Required
 * (and recorded on the submission) whenever the form contains any
 * special-category field. Wording is HR/DPO-supplied; a placeholder is fine
 * until then.
 */
export interface FormConsent {
  required: boolean;
  statement: string;
}

/** The full schema stored in onboarding_form_versions.schema (JSONB). */
export interface FormSchema {
  pages: FormPage[];
  consent?: FormConsent;
}

/**
 * Completion of a part-filled form, derived (not stored) from the inductee's
 * autosaved draft. `required_*` count only the currently-visible required
 * fields, so a true "mandatory fields done" percentage can be shown. `started`
 * is true once any field has an answer or consent has been ticked.
 */
export interface FormProgress {
  required_total: number;
  required_done: number;
  started: boolean;
}

/* -------------------------------------------------------------------------
 * Form + version entities (mirror onboarding documents)
 * -----------------------------------------------------------------------*/

/**
 * What a form is FOR. 'onboarding' forms are self-service tasks assigned to
 * a person and filled in /me. 'kit_handover' forms are desk checklists (e.g.
 * the Fleet vehicle handover) picked up by the named station's issue drawer,
 * completed jointly at the desk and stored as form_submissions rows; they
 * never appear as /me tasks and skip the HR approval flow.
 */
export const FORM_KINDS = ['onboarding', 'kit_handover'] as const;
export type FormKind = (typeof FORM_KINDS)[number];

export interface OnboardingForm {
  id: string;
  /** Stable slug, e.g. 'new-starter-details'. */
  key: string;
  title: string;
  kind: FormKind;
  /** For kind='kit_handover': which station's drawer offers this form. */
  station: string | null;
  created_at: string; // ISO 8601
  archived_at: string | null;
}

export interface OnboardingFormVersion {
  id: string;
  form_id: string;
  /** Monotonic per form, starting at 1. */
  version: number;
  title: string;
  description: string | null;
  schema: FormSchema;
  published_at: string | null;
  created_at: string; // ISO 8601
}

/* -------------------------------------------------------------------------
 * Defaults + helpers
 * -----------------------------------------------------------------------*/

/** Classification a field gets when it declares none. HR + inductee only. */
export const DEFAULT_FIELD_CLASSIFICATION: readonly FieldSurface[] = ['self', 'onboarding'];

/**
 * Desk surfaces: anything that crosses to a mobilisation station. Special-
 * category fields may never cross to one of these, and desk-visible fields
 * are stored plaintext so a station can read them without the key.
 */
export const DESK_SURFACES: readonly FieldSurface[] = FIELD_SURFACES.filter(
  (s) => s !== 'self' && s !== 'onboarding',
);

/** Resolve a field's effective classification (applying the default). */
export function effectiveClassification(field: FormFieldDef): readonly FieldSurface[] {
  if (field.special_category) return DEFAULT_FIELD_CLASSIFICATION;
  return field.classification && field.classification.length > 0
    ? field.classification
    : DEFAULT_FIELD_CLASSIFICATION;
}

/** True if the field's value crosses to any mobilisation desk surface. */
export function isDeskField(field: FormFieldDef): boolean {
  return effectiveClassification(field).some((s) => DESK_SURFACES.includes(s));
}

/* -------------------------------------------------------------------------
 * Schema validator (dependency-free so it is shared by web + server)
 * -----------------------------------------------------------------------*/

export interface FormSchemaValidationResult {
  valid: boolean;
  errors: string[];
}

const KEY_RE = /^[a-z0-9_]+$/;
const SLUG_RE = /^[a-z0-9_-]+$/;

/**
 * Extensions that may never be uploaded, whatever a field's accept list says.
 * The server enforces this again on upload; the builder rejects them early.
 */
export const BLOCKED_UPLOAD_EXTENSIONS: readonly string[] = [
  'exe',
  'msi',
  'bat',
  'cmd',
  'com',
  'scr',
  'pif',
  'cpl',
  'sh',
  'bash',
  'ps1',
  'psm1',
  'vbs',
  'vbe',
  'js',
  'jse',
  'jar',
  'app',
  'dmg',
  'deb',
  'rpm',
  'apk',
  'dll',
  'sys',
  'msc',
  'reg',
  'lnk',
  'hta',
  'wsf',
  'wsh',
  // SVG can carry scripts and event handlers, and the download route
  // serves it inline as image/svg+xml, making a stored-XSS vector that
  // only the CSP currently contains. No legitimate upload task needs
  // vector graphics; photos and scans cover everything.
  'svg',
  'svgz',
];

/** Validate the optional procurement `order` block on a grid row. */
function validateGridRowOrder(
  row: GridRow | undefined,
  where: string,
  allKeys: Set<string>,
  errors: string[],
): void {
  const ord = row?.order;
  if (ord === undefined) return;
  const rowKey = row?.key ?? '?';
  if (typeof ord.item !== 'string' || ord.item.trim() === '') {
    errors.push(`${where}: grid row "${rowKey}" order needs an item name`);
  }
  if (!GRID_ORDER_CATEGORIES.includes(ord.category)) {
    errors.push(`${where}: grid row "${rowKey}" order has an unknown category`);
  }
  if (ord.gate_field_key !== undefined) {
    if (!allKeys.has(ord.gate_field_key)) {
      errors.push(
        `${where}: grid row "${rowKey}" order gate references unknown field "${ord.gate_field_key}"`,
      );
    }
    if (typeof ord.gate_skip_value !== 'string' || ord.gate_skip_value === '') {
      errors.push(`${where}: grid row "${rowKey}" order gate needs a skip value`);
    }
  }
}

function validateField(
  field: unknown,
  index: number,
  pageIdx: number,
  seenKeys: Set<string>,
  allKeys: Set<string>,
  errors: string[],
): void {
  const where = `page ${pageIdx + 1} field ${index + 1}`;
  if (typeof field !== 'object' || field === null) {
    errors.push(`${where}: must be an object`);
    return;
  }
  const f = field as Partial<FormFieldDef>;

  if (typeof f.type !== 'string' || !FORM_FIELD_TYPES.includes(f.type)) {
    errors.push(`${where}: unknown field type "${String(f.type)}"`);
    return;
  }
  if (typeof f.key !== 'string' || !KEY_RE.test(f.key)) {
    errors.push(`${where}: key must match ${KEY_RE} (got "${String(f.key)}")`);
  } else if (seenKeys.has(f.key)) {
    errors.push(`${where}: duplicate field key "${f.key}"`);
  } else {
    seenKeys.add(f.key);
  }
  if (typeof f.label !== 'string' || f.label.trim() === '') {
    errors.push(`${where}: label is required`);
  }

  if (OPTION_FIELD_TYPES.includes(f.type)) {
    if (!Array.isArray(f.options) || f.options.length === 0) {
      errors.push(`${where}: ${f.type} needs at least one option`);
    } else if (
      f.options.some(
        (o) =>
          typeof o?.value !== 'string' ||
          o.value === '' ||
          typeof o?.label !== 'string' ||
          o.label === '',
      )
    ) {
      errors.push(`${where}: every option needs a non-empty value and label`);
    }
  }

  if (f.type === 'grid') {
    const g = f.grid;
    if (!g || !Array.isArray(g.rows) || g.rows.length === 0) {
      errors.push(`${where}: grid needs at least one row`);
    }
    if (!g || !Array.isArray(g.columns) || g.columns.length === 0) {
      errors.push(`${where}: grid needs at least one column`);
    }
    if (g && Array.isArray(g.columns)) {
      for (const [ci, col] of g.columns.entries()) {
        if (typeof col?.key !== 'string' || !KEY_RE.test(col.key)) {
          errors.push(`${where}: grid column ${ci + 1} key must match ${KEY_RE}`);
        }
        if (!GRID_CELL_TYPES.includes(col?.cell)) {
          errors.push(`${where}: grid column ${ci + 1} has an unknown cell type`);
        }
        if (col?.cell === 'select' && (!Array.isArray(col.options) || col.options.length === 0)) {
          errors.push(`${where}: grid select column "${col.key}" needs options`);
        }
        if (col?.mask) {
          if (col.cell === 'select') {
            errors.push(`${where}: grid column "${col.key}" select cells cannot have a mask`);
          } else if (!maskHasToken(col.mask)) {
            errors.push(
              `${where}: grid column "${col.key}" mask needs at least one @ / # / * position`,
            );
          }
        }
      }
    }
    if (g && Array.isArray(g.rows)) {
      for (const [ri, row] of g.rows.entries()) {
        validateGridRowOrder(row, where, allKeys, errors);
        if (row?.cell === undefined) continue; // inherits the column
        if (!GRID_CELL_TYPES.includes(row.cell)) {
          errors.push(`${where}: grid row ${ri + 1} has an unknown cell type`);
        }
        if (row.cell === 'select' && (!Array.isArray(row.options) || row.options.length === 0)) {
          errors.push(`${where}: grid row "${row.key}" is a dropdown and needs options`);
        }
        if (row.mask) {
          if (row.cell === 'select') {
            errors.push(`${where}: grid row "${row.key}" select cells cannot have a mask`);
          } else if (!maskHasToken(row.mask)) {
            errors.push(
              `${where}: grid row "${row.key}" mask needs at least one @ / # / * position`,
            );
          }
        }
      }
    }
  }

  if (f.type === 'damage_markup' && (typeof f.image_key !== 'string' || f.image_key === '')) {
    errors.push(`${where}: damage markup needs a background image`);
  }

  if (f.type === 'file_upload' && Array.isArray(f.accept)) {
    for (const ext of f.accept) {
      if (typeof ext !== 'string' || !/^[a-z0-9]+$/.test(ext)) {
        errors.push(
          `${where}: accept extensions must be lowercase alphanumeric (got "${String(ext)}")`,
        );
      } else if (BLOCKED_UPLOAD_EXTENSIONS.includes(ext)) {
        errors.push(`${where}: file type "${ext}" is not allowed`);
      }
    }
  }

  if (f.classification !== undefined) {
    if (!Array.isArray(f.classification)) {
      errors.push(`${where}: classification must be an array`);
    } else if (f.classification.some((s) => !FIELD_SURFACES.includes(s))) {
      errors.push(`${where}: classification has an unknown surface`);
    }
  }

  // Composite fields hold a structured value the desk crossover can't flatten
  // for display, so they may never be shown on a desk surface.
  if (f.type === 'address' && Array.isArray(f.classification)) {
    if (f.classification.some((s) => DESK_SURFACES.includes(s))) {
      errors.push(`${where}: an address field cannot be shown on a desk`);
    }
  }

  // Special-category fields may never reach a desk surface.
  if (f.special_category && Array.isArray(f.classification)) {
    if (f.classification.some((s) => DESK_SURFACES.includes(s))) {
      errors.push(`${where}: special-category fields cannot be shown on a desk`);
    }
  }

  if (f.visible_if !== undefined) {
    const c = f.visible_if;
    if (typeof c?.field !== 'string' || !allKeys.has(c.field)) {
      errors.push(`${where}: visible_if references unknown field "${String(c?.field)}"`);
    }
    if (!Array.isArray(c?.equals) || c.equals.length === 0) {
      errors.push(`${where}: visible_if needs a non-empty equals list`);
    }
  }

  if (f.mask !== undefined && f.mask !== '') {
    if (typeof f.mask !== 'string') {
      errors.push(`${where}: mask must be a string`);
    } else if (!MASKABLE_FIELD_TYPES.includes(f.type)) {
      errors.push(`${where}: a "${f.type}" field cannot have an input mask`);
    } else if (!maskHasToken(f.mask)) {
      errors.push(`${where}: mask needs at least one @ (letter), # (number) or * position`);
    }
  }
}

/**
 * Validate a form schema (the JSONB persisted per version). Pure and
 * dependency-free so the builder and the server share one source of truth.
 * Collects every problem rather than failing on the first.
 */
export function validateFormSchema(schema: unknown): FormSchemaValidationResult {
  const errors: string[] = [];
  if (typeof schema !== 'object' || schema === null) {
    return { valid: false, errors: ['schema must be an object'] };
  }
  const s = schema as Partial<FormSchema>;
  if (!Array.isArray(s.pages) || s.pages.length === 0) {
    errors.push('form needs at least one page');
    return { valid: false, errors };
  }

  // First pass: collect all field keys (so a field visible_if can reference
  // forward) and which page each key is on (so a page visible_if can require an
  // earlier-page controlling field).
  const allKeys = new Set<string>();
  const keyPage = new Map<string, number>();
  for (const [pi, page] of s.pages.entries()) {
    if (page && Array.isArray(page.fields)) {
      for (const f of page.fields) {
        const k = (f as Partial<FormFieldDef>)?.key;
        if (typeof k === 'string') {
          allKeys.add(k);
          if (!keyPage.has(k)) keyPage.set(k, pi);
        }
      }
    }
  }

  const seenKeys = new Set<string>();
  let fieldCount = 0;
  let hasSpecialCategory = false;
  for (const [pi, page] of s.pages.entries()) {
    if (typeof page !== 'object' || page === null || !Array.isArray(page.fields)) {
      errors.push(`page ${pi + 1}: must have a fields array`);
      continue;
    }
    const pc = page.visible_if;
    if (pc !== undefined) {
      if (typeof pc?.field !== 'string' || !allKeys.has(pc.field)) {
        errors.push(`page ${pi + 1}: visible_if references unknown field "${String(pc?.field)}"`);
      } else if ((keyPage.get(pc.field) ?? pi) >= pi) {
        errors.push(`page ${pi + 1}: visible_if must reference a field on an earlier page`);
      }
      if (!Array.isArray(pc?.equals) || pc.equals.length === 0) {
        errors.push(`page ${pi + 1}: visible_if needs a non-empty equals list`);
      }
    }
    for (const [fi, field] of page.fields.entries()) {
      validateField(field, fi, pi, seenKeys, allKeys, errors);
      const ft = (field as Partial<FormFieldDef>)?.type;
      if (typeof ft === 'string' && !STATIC_FIELD_TYPES.includes(ft as FormFieldType)) {
        fieldCount += 1;
      }
      if ((field as Partial<FormFieldDef>)?.special_category) hasSpecialCategory = true;
    }
  }

  if (fieldCount === 0) {
    errors.push('form needs at least one input field');
  }

  // A form holding special-category data must carry an explicit consent step.
  if (hasSpecialCategory) {
    const c = s.consent;
    if (!c || c.required !== true || typeof c.statement !== 'string' || c.statement.trim() === '') {
      errors.push(
        'forms with special-category fields require a consent statement (consent.required = true with statement text)',
      );
    }
  }

  return { valid: errors.length === 0, errors };
}

/** True if `key` is a valid form slug (used by the create endpoint). */
export function isValidFormKey(key: string): boolean {
  return SLUG_RE.test(key);
}

/* -------------------------------------------------------------------------
 * Ordering helpers (the Kit to order screen consumes these)
 * -----------------------------------------------------------------------*/

/**
 * A grid row tagged for ordering, resolved with the coordinates needed to read
 * its submitted size out of a submission's grid value
 * (Record<rowKey, Record<colKey, string>>). `col_key` is the grid's first
 * column — ordering grids are expected to have a single size column.
 */
export interface OrderRowRef {
  field_key: string;
  row_key: string;
  col_key: string;
  label: string;
  order: GridRowOrder;
}

/** Every order-tagged grid row across a form schema, in document order. */
export function collectOrderRows(schema: FormSchema | undefined): OrderRowRef[] {
  const refs: OrderRowRef[] = [];
  if (!schema || !Array.isArray(schema.pages)) return refs;
  for (const page of schema.pages) {
    if (!page || !Array.isArray(page.fields)) continue;
    for (const field of page.fields) {
      if (field.type !== 'grid' || !field.grid) continue;
      const colKey = field.grid.columns[0]?.key;
      if (!colKey) continue;
      for (const row of field.grid.rows) {
        if (!row.order) continue;
        refs.push({
          field_key: field.key,
          row_key: row.key,
          col_key: colKey,
          label: row.label,
          order: row.order,
        });
      }
    }
  }
  return refs;
}

/** True if a form schema has any order-tagged grid row. */
export function isOrderingSource(schema: FormSchema | undefined): boolean {
  return collectOrderRows(schema).length > 0;
}
